<template>
  <VcLayout
    v-if="isLoading"
    align-center
    :class="`VcMenuItem MenuItemSkeletonContainer ${size} ${flavor}`"
    :data-qa="dataQa">
    <VSkeletonLoader class="VcMenuItem-skeleton" type="image" width="20" height="20" />
    <VSkeletonLoader type="list-item" width="100%" />
  </VcLayout>
  <VcLayout
    v-else-if="submenuItems.length === 0"
    :class="`VcMenuItem VcMenuItem-simple ${size} ${flavor} ${isSelected ? 'selected' : ''} ${
      dense ? 'dense' : ''
    }`"
    align-center
    :tabindex="0"
    :data-qa="dataQa"
    @keyup.enter="$emit('click')"
    @click="$emit('click')">
    <VcIcon
      v-if="icon"
      :class="`VcMenuItem-icon ${flavor}`"
      :size="size === 'md' ? 20 : 16"
      :color="textColor">
      {{ icon }}
    </VcIcon>
    <slot>
      <VcLayout align-baseline>
        <div :class="`VcMenuItem-text ${flavor} VcMenuItem-userSelectNone`">
          {{ label }}
        </div>
        <div v-if="badgeText" :class="`VcMenuItem-badge badge-color-${badgeColor} ps-5`">
          {{ badgeText }}
        </div>
      </VcLayout>
    </slot>
    <VcLayout class="VcMenuItem-right-area justify-center align-center">
      <slot name="actions" />
    </VcLayout>
  </VcLayout>
  <v-expansion-panels
    v-else
    flat
    hover
    :value="isExpanded ? 0 : undefined"
    :class="`${flavor}`"
    :data-qa="dataQa"
    @change="$emit('change', !isExpanded)">
    <v-expansion-panel class="VcMenuItem--expansionPanel">
      <v-expansion-panel-header align-center :class="`VcMenuItem VcMenuItem-complex ${flavor}`">
        <VcLayout align-center>
          <VcIcon
            v-if="icon"
            :class="`VcMenuItem-icon ${flavor}`"
            :size="size === 'md' ? 20 : 16"
            :color="textColor">
            {{ icon }}
          </VcIcon>
          <slot>
            <VcLayout align-center :class="`VcMenuItem ${size}  ${flavor}`">
              <div :class="`VcMenuItem-text ${flavor}`">
                {{ label }}
              </div>
              <div v-if="badgeText" :class="`VcMenuItem-badge badge-color-${badgeColor} ps-5`">
                {{ badgeText }}
              </div>
            </VcLayout>
          </slot>
        </VcLayout>
        <template #actions>
          <VcIcon :size="flavor === 'desktop' ? 16 : 24" :color="textColor"> $chevron_down </VcIcon>
        </template>
      </v-expansion-panel-header>
      <v-expansion-panel-content
        v-for="(item, index) in submenuItems"
        :key="item.label"
        eager
        :class="`${isSelected && selectedSubitem === index ? 'selected' : ''} ${flavor}`">
        <VcLayout
          :class="`VcMenuItem-subitem VcMenuItem-userSelectNone ps-6 ${size} ${flavor}`"
          :data-qa="`${dataQa}-subitem-${item.name}`"
          align-center
          @click="$emit('onSubmenuClicked', item, index)">
          <div :class="`VcMenuItem-text ${flavor}`">
            {{ item.label }}
          </div>
          <div v-if="item.badgeText" :class="`VcMenuItem-badge badge-color-orange ps-5`">
            {{ item.badgeText }}
          </div>
        </VcLayout>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import VcLayout from '../VcLayout/VcLayout.vue';
import VcIcon from '../VcIcon/VcIcon.vue';

export default {
  name: 'VcMenuItem',
  components: { VcIcon, VcLayout },
  model: {
    prop: 'isExpanded',
    event: 'change',
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    badgeText: {
      type: String,
    },
    badgeColor: {
      type: String,
      default: 'orange',
      validator: (value) => ['orange'].includes(value),
    },
    submenuItems: {
      type: Array,
      default: () => [],
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    selectedSubitem: {
      type: Number,
    },
    size: {
      type: String,
      default: 'md',
      validator: (prop) => ['sm', 'md'].includes(prop),
    },
    flavor: {
      type: String,
      default: 'mobile',
      validator: (value) => ['mobile', 'desktop'].includes(value),
    },
    dense: {
      type: Boolean,
      default: false,
    },
    dataQa: {
      type: String,
      default: 'VcMenuItem',
    },
  },
  computed: {
    textColor() {
      return this.flavor === 'desktop' ? 'var(--v-text-base)' : 'black';
    },
  },
};
</script>

<style lang="scss" scoped>
.VcMenuItem {
  height: var(--size-value13);
  min-height: unset;
  font-weight: var(--font-weight-medium2);

  &.desktop {
    display: flex;
    color: var(--v-text-base);
    height: var(--size-value10);

    :hover {
      .VcMenuItem-icon,
      .VcMenuItem-text {
        opacity: unset;
      }
    }
  }
}

.MenuItemSkeletonContainer {
  padding: 0 var(--size-value5);
}

.VcMenuItem-skeleton {
  margin-inline-end: var(--size-value5);
}

::v-deep {
  .v-skeleton-loader__list-item {
    background-color: transparent !important;
  }
}

.VcMenuItem-simple {
  &:not(.dense) {
    padding: 0 var(--size-value5);
  }
  border-inline-start: 3px solid transparent;
  transition: border-inline-start 250ms, background-color 250ms;

  &:hover {
    background: rgba(0, 0, 0, 0.04);

    .VcMenuItem-icon,
    .VcMenuItem-text {
      opacity: unset;
    }
  }

  &.desktop {
    &:not(.dense) {
      padding-inline: var(--size-value5) var(--size-value2);
    }

    &:hover {
      background: var(--gray-light-opacity-1);
      background: var(--v-secondary-base-opacity-14);
      cursor: pointer;
    }
  }

  &:active {
    background: rgba(0, 0, 0, 0.12);
    background: var(--v-secondary-base-opacity-20);
    transition: 0.3s background-color;
  }
}

.VcMenuItem--expansionPanel {
  background-color: transparent !important;
}

.VcMenuItem-complex {
  padding: var(--size-value4) var(--size-value5);
  ::v-deep {
    .v-expansion-panel-header__icon {
      flex: 0 0 var(--size-value9);
      justify-content: center;
      align-items: center;
    }
  }
}

.v-expansion-panels:not(.v-expansion-panels--accordion):not(.v-expansion-panels--tile)
  > .v-expansion-panel--active {
  border-radius: 0;
}

.v-expansion-panels {
  border-radius: 0;
}

.desktop {
  ::v-deep {
    .v-expansion-panel-header {
      padding: unset;
      padding-inline: var(--size-value5) var(--size-value2);
      border-inline-start: 3px solid transparent;
      &::before {
        background-color: transparent;
      }
      &--active {
        background: var(--v-secondary-base-opacity-20);
      }
      &:hover {
        background: var(--gray-light-opacity-1);
        background: var(--v-secondary-base-opacity-14);
        cursor: pointer;
      }
    }
  }
}

.VcMenuItem-icon {
  margin-inline-end: var(--size-value4);

  &.desktop {
    flex: 0 0 var(--size-value8);
    margin-inline-end: var(--size-value3);
    opacity: 72%;
  }
}

.VcMenuItem-text {
  font-size: 15px;

  &.desktop {
    opacity: 87%;
  }
}

.desktop {
  .VcMenuItem-text {
    font-size: 13px;
  }
}

.VcMenuItem-userSelectNone {
  user-select: none;
}

.VcMenuItem-badge {
  font-size: var(--font-size-x-small);
  font-weight: var(--font-weight-medium2);

  &.badge-color-orange {
    color: var(--orange);
  }
}

.VcMenuItem-right-area {
  flex: 0 0 var(--size-value9);
}

.v-expansion-panel-content {
  border-inline-start: 3px solid transparent;
  background-color: transparent;
  transition: border-inline-start 250ms, background-color 250ms;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    background: var(--v-secondary-base-opacity-14);
  }

  ::v-deep .v-expansion-panel-content__wrap {
    padding: var(--size-value2) var(--size-value6);
  }
}

.desktop {
  .v-expansion-panel-content {
    &:not(.selected) {
      background: var(--gray-light-opacity-3);
      background: var(--v-secondary-base-opacity-4);
    }

    &:hover {
      background: var(--gray-light-opacity-2);
      background: var(--v-secondary-base-opacity-14);
    }

    ::v-deep .v-expansion-panel-content__wrap {
      padding: var(--size-value2) var(--size-value10);
    }
  }
}

.VcMenuItem-subitem {
  height: var(--size-value8);
  padding: 0;
  font-size: var(--font-size-small2);

  &.desktop {
    height: var(--size-value6);
    color: var(--v-text-base);
    margin: -8px 0px;
    padding: var(--size-value5) var(--size-value0);

    &:hover {
      cursor: pointer;
      .VcMenuItem-text {
        opacity: unset;
      }
    }
  }
}

.sm {
  &.VcMenuItem {
    height: var(--size-value10);
  }

  .VcMenuItem-text {
    font-size: var(--font-size-x-small);
    font-weight: var(--font-weight-medium2);
  }
}

.selected {
  border-inline-start: 3px solid var(--v-secondary-base);
  background-color: var(--gray-light-opacity-1);

  &.desktop {
    background-color: var(--v-secondary-base-opacity-20);
    .VcMenuItem-text,
    .VcMenuItem-icon {
      opacity: unset;
    }
  }
}
</style>
